import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AssistantMark } from './AssistantMark';
interface ThinkingIndicatorProps {
  /** When true, cycles through job-match phrasing instead of the default set. */
  roleMatch?: boolean;
}
const DEFAULT_PHASES = ['Thinking…', "Reading Mateo's portfolio…", 'Pulling relevant case studies…', 'Composing a response…'];
const ROLE_MATCH_PHASES = ['Reading the role…', "Mapping Mateo's experience…", 'Scoring relevant case studies…', 'Writing the match…'];
/**
 * Crafted multi-stage "spooling up" indicator.
 * Cycles short status phrases with a quiet animated mark and a shimmer
 * skeleton of the incoming response — monochrome, on-brand, no bouncing dots.
 */
export function ThinkingIndicator({
  roleMatch = false
}: ThinkingIndicatorProps) {
  const phases = roleMatch ? ROLE_MATCH_PHASES : DEFAULT_PHASES;
  const [phaseIndex, setPhaseIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setPhaseIndex(i => (i + 1) % phases.length);
    }, 1900);
    return () => clearInterval(id);
  }, [phases.length]);
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="flex gap-3" role="status" aria-live="polite" data-id="element-477">
      <AssistantMark size={28} animated data-id="element-478" />

      <div className="flex-1 min-w-0 pt-0.5" data-id="element-479">
        {/* Cycling status phrase */}
        <div className="h-5 mb-3 overflow-hidden" data-id="element-480">
          <AnimatePresence mode="wait" data-id="element-481">
            <motion.span key={phaseIndex} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.32,
            ease: [0.23, 1, 0.32, 1]
          }} className="block text-sm text-white/55" data-id="element-482">
              {phases[phaseIndex]}
            </motion.span>
          </AnimatePresence>
        </div>

        {/* Shimmer skeleton of the incoming answer */}
        <div className="space-y-2.5 max-w-[420px]" data-id="element-483">
          {['92%', '78%', '60%'].map((w, i) => <div key={i} className="thinking-shimmer h-3 rounded-full" style={{
          width: w,
          animationDelay: `${i * 0.18}s`
        }} data-id="element-484" />)}
        </div>
      </div>

      <style data-id="element-485">{`
        .thinking-shimmer {
          background: linear-gradient(
            90deg,
            rgba(255,255,255,0.05) 0%,
            rgba(255,255,255,0.14) 50%,
            rgba(255,255,255,0.05) 100%
          );
          background-size: 200% 100%;
          animation: thinkingShimmer 1.4s ease-in-out infinite;
        }
        @keyframes thinkingShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </motion.div>;
}